/**
 * Pages do not scroll the window: the platform's content column (beside the global nav) and the
 * admin layout's content box are the scrolling elements (`routes/_protected/route.css.ts`,
 * `admin/route.css.ts`). The router's scroll reset (`resetScroll`, on by default for every
 * navigation) only knows the window unless it is told otherwise, so without these selectors a
 * client-side navigation left the new page scrolled wherever the old one was, and only a reload
 * started it at the top. Each scroller carries `data-page-scroller`; the router resets every one it
 * finds. An in-page control (a filter, a tab, the brand row) passes `resetScroll: false`.
 */

/** @desc The attribute a page-level scroller carries, valued by which layout owns it. */
export const PAGE_SCROLLER_ATTRIBUTE = 'data-page-scroller';

export const PageScrollers = {
  /** The platform's content column beside the global nav. */
  Platform: 'platform',
  /** The admin layout's content box, nested inside the platform column. */
  Admin: 'admin',
  /**
   * Automations → Chat's transcript. That page is a fixed-height column like the Agent chat —
   * header, conversation, composer pinned at the foot — so the admin box above never overflows
   * and the transcript is the page's only scroller. Its in-page conversation switch passes
   * `resetScroll: false`; `useChatScroll` lands each conversation at its latest message.
   */
  AutomationsChat: 'automations-chat',
} as const;
export type PageScroller = (typeof PageScrollers)[keyof typeof PageScrollers];

/** @desc Spread onto a scroller: `<Stack {...pageScrollerProps(PageScrollers.Platform)}>`. */
export const pageScrollerProps = (scroller: PageScroller) => ({
  [PAGE_SCROLLER_ATTRIBUTE]: scroller,
});

/** @desc The router's `scrollToTopSelectors`: the window plus every page-level scroller. */
export const pageScrollToTopSelectors: string[] = [
  'window',
  ...Object.values(PageScrollers).map((scroller) => `[${PAGE_SCROLLER_ATTRIBUTE}="${scroller}"]`),
];

/**
 * @desc Whether a listing's search patch is a change of page, which starts the reader at the top
 * like any new page, rather than an in-place control (a filter, the search box, sort, layout),
 * which must leave the reader where they are. Pass it as the navigation's `resetScroll`.
 */
export const isPageTurn = (patch: Record<string, unknown>): boolean => {
  const keys = Object.keys(patch);
  return keys.length > 0 && keys.every((key) => key === 'page');
};
